import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import CaseCard from '@/components/feature/CaseCard';
import useSeo, { SITE_NAME, SITE_ORIGIN } from '@/hooks/useSeo';
import { cases, categories, evidenceTypes, evidenceLabelMap, type EvidenceKey } from '@/mocks/cases';
import CaseFilters from './components/CaseFilters';

const PAGE_SIZE = 6;

export default function Cases() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const category = searchParams.get('cat') ?? 'all';
  const evidence = searchParams.get('ev') ?? 'all';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const [notice, setNotice] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return cases.filter((item) => {
      const matchCat = category === 'all' || item.categoryId === category;
      const matchEv = evidence === 'all' || item.evidence.includes(evidence as EvidenceKey);
      const matchKw =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCat && matchEv && matchKw;
    });
  }, [keyword, category, evidence]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages && filtered.length > 0) {
      const next = new URLSearchParams(searchParams);
      next.delete('page');
      setSearchParams(next, { replace: true });
    }
  }, [page, totalPages, filtered.length, searchParams, setSearchParams]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete('page');
    setSearchParams(next);
  }

  function goPage(target: number) {
    const next = new URLSearchParams(searchParams);
    if (target <= 1) {
      next.delete('page');
    } else {
      next.set('page', String(target));
    }
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const activeCategory = categories.find((cat) => cat.id === category);
  const activeEvidence = evidenceTypes.find((type) => type.key === evidence);

  const filterLabelParts: string[] = [];
  if (activeCategory) filterLabelParts.push(activeCategory.name);
  if (activeEvidence) filterLabelParts.push(evidenceLabelMap[activeEvidence.key as EvidenceKey]);
  if (keyword) filterLabelParts.push(`关键词「${keyword}」`);
  const filterLabel = filterLabelParts.length ? filterLabelParts.join(' · ') : '全部内容';

  useSeo({
    title: `爆料列表 - ${filterLabel} | ${SITE_NAME}`,
    description: `浏览网红爆料网收录的爆料材料合集，当前筛选：${filterLabel}。内容涵盖录音、视频证据、合同照片与转账记录，每条均标注核实状态。`,
    keywords: '网红爆料列表,爆料合集,实锤材料,证据类型筛选,爆料分类',
    path: '/cases',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `爆料列表 - ${filterLabel}`,
        url: `${SITE_ORIGIN}/cases`,
        inLanguage: 'zh-CN',
        description: '按主题分类与证据类型整理的爆料材料集合页。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '爆料材料列表',
        numberOfItems: pageItems.length,
        itemListElement: pageItems.map((item, index) => ({
          '@type': 'ListItem',
          position: (currentPage - 1) * PAGE_SIZE + index + 1,
          name: item.title,
          url: `${SITE_ORIGIN}/case/${item.slug}`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: SITE_ORIGIN },
          { '@type': 'ListItem', position: 2, name: '爆料列表', item: `${SITE_ORIGIN}/cases` },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '爆料列表' }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">爆料材料列表</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            你可以按主题分类、证据类型或关键词查找已收录的爆料材料。每条内容都会标注当前核实状态，未核实部分会明确写出。
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={`#filter-top`}
                onClick={(e) => {
                  e.preventDefault();
                  updateParam('cat', cat.id);
                }}
                className="rounded-md bg-background-50 px-2.5 py-1.5 text-foreground-700 transition-colors hover:bg-secondary-100"
              >
                #{cat.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section id="filter-top" className="py-8 md:py-10">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <CaseFilters
            keyword={keyword}
            category={category}
            evidence={evidence}
            onKeyword={(v) => {
              updateParam('q', v);
              setNotice(v ? `已按关键词「${v}」筛选` : '');
            }}
            onCategory={(v) => {
              updateParam('cat', v);
              setNotice('');
            }}
            onEvidence={(v) => {
              updateParam('ev', v);
              setNotice('');
            }}
          />

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-foreground-600">
              当前筛选：<span className="font-medium text-foreground-900">{filterLabel}</span>
              ，共 <span className="font-medium text-primary-600">{filtered.length}</span> 条材料
            </p>
            {(keyword || category !== 'all' || evidence !== 'all') && (
              <button
                type="button"
                onClick={() => {
                  setSearchParams(new URLSearchParams());
                  setNotice('');
                }}
                className="flex cursor-pointer items-center gap-1 text-sm text-foreground-500 hover:text-primary-600"
              >
                <i className="ri-close-circle-line"></i>
                清除筛选
              </button>
            )}
          </div>

          {notice && (
            <p className="mt-3 flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2 text-sm text-secondary-900">
              <i className="ri-information-line"></i>
              {notice}
            </p>
          )}

          {pageItems.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {pageItems.map((item) => (
                <CaseCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                <i className="ri-search-eye-line"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有找到匹配的爆料</h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                试试更换关键词，或者放宽主题分类与证据类型的限制。也可以直接投稿，我们会帮你整理材料。
              </p>
              <button
                type="button"
                onClick={() => setSearchParams(new URLSearchParams())}
                className="mt-5 cursor-pointer rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                重置筛选条件
              </button>
            </div>
          )}

          {totalPages > 1 && (
            <nav className="mt-10 flex items-center justify-center gap-2" aria-label="分页导航">
              <button
                type="button"
                onClick={() => goPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="上一页"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => goPage(p)}
                  aria-current={p === currentPage ? 'page' : undefined}
                  className={`h-9 min-w-9 cursor-pointer rounded-md px-3 text-sm transition-colors ${
                    p === currentPage
                      ? 'bg-primary-600 text-background-50'
                      : 'border border-background-300 text-foreground-700 hover:border-primary-400'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="下一页"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </nav>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}